// Pricing model, validators and payload builder for the enrollment wizard.
// `isValidDiscount` is the shared predicate lifted out of
// Account/ProductPricing.js so both screens enforce the same bounds.

export const OFFER_TYPES = {
  DISCOUNT_PERCENTAGE: "DiscountPercentage",
  PRICE_PER_SQUARE: "PricePerSquare",
};

export const DISCOUNT_MODES = {
  DEFAULT_FOR_ALL: "DefaultForAll",
  PER_PRODUCT_GROUP: "PerProductGroup",
  PER_PRODUCT_CODE: "PerProductCode",
};

export const OFFER_TYPE_OPTIONS = [
  { value: OFFER_TYPES.DISCOUNT_PERCENTAGE, label: "Discount percentage" },
  { value: OFFER_TYPES.PRICE_PER_SQUARE, label: "Price per square" },
];

export const DISCOUNT_MODE_OPTIONS = [
  { value: DISCOUNT_MODES.DEFAULT_FOR_ALL, label: "One default discount for all" },
  { value: DISCOUNT_MODES.PER_PRODUCT_GROUP, label: "Unique discount per product group" },
  { value: DISCOUNT_MODES.PER_PRODUCT_CODE, label: "Custom per product code" },
];

export const isValidDiscount = (value) => {
  const text = String(value ?? "").trim();
  return (
    /^\d+(?:\.\d{1,2})?$/.test(text) && Number(text) >= 0 && Number(text) <= 100
  );
};

export const isValidPricePerSquare = (value) => {
  const text = String(value ?? "").trim();
  return /^\d+(?:\.\d{1,2})?$/.test(text) && Number(text) > 0;
};

export const createEmptyPricingState = () => ({
  OfferType: OFFER_TYPES.DISCOUNT_PERCENTAGE,
  DiscountMode: DISCOUNT_MODES.DEFAULT_FOR_ALL,
  DefaultDiscountPercentage: "",
  // Values are kept per group / per product code even when the active mode does
  // not use them, so switching modes is never destructive (plan section 6).
  GroupDiscounts: {},
  GroupPricePerSquare: {},
  ProductDiscounts: {},
});

const toNumberOrNull = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

export const clearPricingForGroup = (pricing, productGroupId) => {
  const key = String(productGroupId);
  const groupDiscounts = { ...pricing.GroupDiscounts };
  const groupPricePerSquare = { ...pricing.GroupPricePerSquare };
  const productDiscounts = { ...pricing.ProductDiscounts };
  delete groupDiscounts[key];
  delete groupPricePerSquare[key];
  delete productDiscounts[key];
  return {
    ...pricing,
    GroupDiscounts: groupDiscounts,
    GroupPricePerSquare: groupPricePerSquare,
    ProductDiscounts: productDiscounts,
  };
};

// Every selected group must carry a complete, valid value for the active mode
// before the wizard lets the user submit.
export const isPricingComplete = (pricing, selectedGroups = []) => {
  if (selectedGroups.length === 0) return false;

  if (pricing.OfferType === OFFER_TYPES.PRICE_PER_SQUARE) {
    return selectedGroups.every((group) =>
      isValidPricePerSquare(pricing.GroupPricePerSquare?.[group.ProductGroupID]),
    );
  }

  if (pricing.DiscountMode === DISCOUNT_MODES.DEFAULT_FOR_ALL) {
    return isValidDiscount(pricing.DefaultDiscountPercentage);
  }

  if (pricing.DiscountMode === DISCOUNT_MODES.PER_PRODUCT_GROUP) {
    return selectedGroups.every((group) =>
      isValidDiscount(pricing.GroupDiscounts?.[group.ProductGroupID]),
    );
  }

  return selectedGroups.every((group) =>
    (group.Products || []).length > 0 &&
    (group.Products || []).every((product) =>
      isValidDiscount(
        pricing.ProductDiscounts?.[group.ProductGroupID]?.[product.ProductID],
      ),
    ),
  );
};

// Only fields valid for the active mode reach the payload, even though the
// component keeps the rest in state.
export const buildPricingPayload = (pricing, selectedGroups = []) => {
  const isPricePerSquare = pricing.OfferType === OFFER_TYPES.PRICE_PER_SQUARE;
  const mode = isPricePerSquare ? null : pricing.DiscountMode;

  return {
    OfferType: pricing.OfferType,
    DiscountMode: mode,
    DefaultDiscountPercentage:
      mode === DISCOUNT_MODES.DEFAULT_FOR_ALL
        ? toNumberOrNull(pricing.DefaultDiscountPercentage)
        : null,
    ProductGroups: selectedGroups.map((group) => ({
      ProductGroupID: group.ProductGroupID,
      ProductGroupName: group.ProductGroupName,
      ManufacturerID: group.ManufacturerID,
      ManufacturerName: group.ManufacturerName,
      QCode: group.QCode,
      Rate: group.Rate,
      DiscountPercentage:
        mode === DISCOUNT_MODES.PER_PRODUCT_GROUP
          ? toNumberOrNull(pricing.GroupDiscounts?.[group.ProductGroupID])
          : null,
      PricePerSquare: isPricePerSquare
        ? toNumberOrNull(pricing.GroupPricePerSquare?.[group.ProductGroupID])
        : null,
      Products:
        mode === DISCOUNT_MODES.PER_PRODUCT_CODE
          ? (group.Products || []).map((product) => ({
              ProductID: product.ProductID,
              ProductCode: product.ProductCode,
              ProductName: product.ProductName,
              Units: product.Units,
              StandardPrice: product.StandardPrice,
              DiscountPercentage: toNumberOrNull(
                pricing.ProductDiscounts?.[group.ProductGroupID]?.[
                  product.ProductID
                ],
              ),
            }))
          : [],
    })),
  };
};

export const describeOfferType = (payload) => {
  if (payload?.OfferType === OFFER_TYPES.PRICE_PER_SQUARE) {
    return "Price per square";
  }
  const label = DISCOUNT_MODE_OPTIONS.find(
    (option) => option.value === payload?.DiscountMode,
  )?.label;
  return label ? `Discount percentage — ${label}` : "Discount percentage";
};
